import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API, useAuth } from "../App";
import { Wrench, Mail, LogIn, Clock } from "lucide-react";

// Paths that are always reachable even during maintenance (so admins can log in).
const ALLOW_PATHS = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/accept-invitation",
];

function useCountdown(endIso) {
  const [label, setLabel] = useState("");
  useEffect(() => {
    if (!endIso) {
      setLabel("");
      return;
    }
    const tick = () => {
      const diff = new Date(endIso).getTime() - Date.now();
      if (isNaN(diff)) {
        setLabel("");
        return;
      }
      if (diff <= 0) {
        setLabel("00:00:00");
        return;
      }
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);
      const pad = (n) => String(n).padStart(2, "0");
      setLabel(d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endIso]);
  return label;
}

export default function MaintenanceGate({ children }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // re-fetch status whenever auth state or path changes
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/maintenance/status`);
        if (!cancelled) setStatus(res.data);
      } catch {
        if (!cancelled) setStatus({ is_enabled: false });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.user_id, location.pathname]);

  // Session bypass via ?bypass=1 (still requires server to confirm once logged-in)
  useEffect(() => {
    if (location.search.includes("bypass=1")) {
      sessionStorage.setItem("maintenance_bypass_attempt", "1");
    }
  }, [location.search]);

  // Always allow whitelisted paths (login/signup/etc) so admins can authenticate
  const pathAllowed = ALLOW_PATHS.some((p) => location.pathname.startsWith(p));

  if (!loaded) return null; // brief flash avoidance
  if (!status?.is_enabled) return children;
  if (status.can_bypass) return children;
  if (pathAllowed) return children;

  const title = (lang === "el" && status.title_el) || status.title || "Under Maintenance";
  const message =
    (lang === "el" && status.message_el) ||
    status.message ||
    "We'll be right back.";

  return (
    <MaintenanceScreen
      title={title}
      message={message}
      contactEmail={status.contact_email}
      endTime={status.end_time}
      logoUrl={status.logo_url}
    />
  );
}

function MaintenanceScreen({ title, message, contactEmail, endTime, logoUrl }) {
  const { t } = useTranslation();
  const countdown = useCountdown(endTime);
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-emerald-950 text-white p-6"
      data-testid="maintenance-screen"
    >
      <div className="max-w-xl w-full bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-10 text-center shadow-2xl">
        {logoUrl ? (
          <img src={logoUrl} alt="logo" className="mx-auto h-16 mb-6 object-contain" />
        ) : (
          <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mb-6">
            <Wrench className="h-8 w-8 text-emerald-400" />
          </div>
        )}

        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4" data-testid="maintenance-title">
          {title}
        </h1>
        <p className="text-base text-white/80 leading-relaxed mb-8 whitespace-pre-line" data-testid="maintenance-message">
          {message}
        </p>

        {countdown && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-400/30 mb-8" data-testid="maintenance-countdown">
            <Clock className="h-4 w-4 text-emerald-300" />
            <span className="font-mono text-sm text-emerald-200">
              {t("maintenance:back_in", { defaultValue: "Back in" })} {countdown}
            </span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
          {contactEmail && (
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-sm transition-colors"
              data-testid="maintenance-contact-link"
            >
              <Mail className="h-4 w-4" />
              {contactEmail}
            </a>
          )}
          <Link
            to="/login?bypass=1"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-medium text-sm transition-colors"
            data-testid="maintenance-admin-login-link"
          >
            <LogIn className="h-4 w-4" />
            {t("maintenance:admin_login", { defaultValue: "Admin Login" })}
          </Link>
        </div>
      </div>
    </div>
  );
}
